/**
 * Report utility functions for formatting and calculations
 */

/**
 * Format currency value
 */
export const formatCurrency = (value: number, currency: string = 'HKD'): string => {
  return new Intl.NumberFormat('en-HK', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format number with commas
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Format date to locale string
 */
export const formatDate = (date: string | Date, format: 'short' | 'long' = 'short'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (format === 'long') {
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date range string
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

/**
 * Calculate percentage change
 */
export const calculatePercentageChange = (current: number, previous: number): string => {
  if (previous === 0) return '+0%';
  const change = ((current - previous) / previous) * 100;
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
};

/**
 * Format energy value (kWh)
 */
export const formatEnergy = (kwh: number): string => {
  return `${formatNumber(kwh, 2)} kWh`;
};

/**
 * Format duration in seconds to human readable
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Format date to YYYY-MM-DD in local timezone (no UTC conversion)
 */
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get date range presets
 */
export const getDateRangePresets = (translate?: (key: string, options?: any) => string) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const last7Days = new Date(today);
  last7Days.setDate(last7Days.getDate() - 7);

  const last30Days = new Date(today);
  last30Days.setDate(last30Days.getDate() - 30);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  return {
    today: {
      start: formatLocalDate(today),
      end: formatLocalDate(today),
      label: translate ? translate('reports.periods.today', { fallback: 'Today' }) : 'Today',
    },
    yesterday: {
      start: formatLocalDate(yesterday),
      end: formatLocalDate(yesterday),
      label: translate ? translate('reports.periods.yesterday', { fallback: 'Yesterday' }) : 'Yesterday',
    },
    last7Days: {
      start: formatLocalDate(last7Days),
      end: formatLocalDate(today),
      label: translate ? translate('reports.periods.last7Days', { fallback: 'Last 7 Days' }) : 'Last 7 Days',
    },
    last30Days: {
      start: formatLocalDate(last30Days),
      end: formatLocalDate(today),
      label: translate ? translate('reports.periods.last30Days', { fallback: 'Last 30 Days' }) : 'Last 30 Days',
    },
    thisMonth: {
      start: formatLocalDate(thisMonthStart),
      end: formatLocalDate(today),
      label: translate ? translate('reports.periods.thisMonth', { fallback: 'This Month' }) : 'This Month',
    },
    lastMonth: {
      start: formatLocalDate(lastMonthStart),
      end: formatLocalDate(lastMonthEnd),
      label: translate ? translate('reports.periods.lastMonth', { fallback: 'Last Month' }) : 'Last Month',
    },
  };
};

/**
 * Aggregate data by time period
 */
export const aggregateByPeriod = (
  data: any[],
  dateField: string,
  valueField: string,
  period: 'day' | 'week' | 'month'
) => {
  const aggregated = new Map<string, number>();

  data.forEach(item => {
    const date = new Date(item[dateField]);
    let key: string;

    if (period === 'day') {
      key = date.toISOString().split('T')[0];
    } else if (period === 'week') {
      // Get Monday of the week
      const monday = new Date(date);
      monday.setDate(date.getDate() - date.getDay() + 1);
      key = monday.toISOString().split('T')[0];
    } else {
      // month
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }

    const currentValue = aggregated.get(key) || 0;
    aggregated.set(key, currentValue + (item[valueField] || 0));
  });

  return Array.from(aggregated.entries())
    .map(([date, value]) => ({ date, value }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Calculate summary statistics
 */
export const calculateSummaryStats = (values: number[]) => {
  if (values.length === 0) {
    return {
      sum: 0,
      average: 0,
      min: 0,
      max: 0,
      count: 0,
    };
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  const average = sum / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);

  return {
    sum,
    average,
    min,
    max,
    count: values.length,
  };
};
